import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { studioSecurity } from '@/lib/api';

interface SecurityCheck {
  id: string;
  name: string;
  passed: boolean;
  severity: 'critical' | 'high' | 'medium' | 'low';
  message: string;
}

interface PostureData {
  project_id: string;
  score: number;
  status: string;
  checks: SecurityCheck[];
  summary: { total: number; passed: number; failed: number; critical_failed: number };
}

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'text-red-600 bg-red-50',
  high: 'text-orange-600 bg-orange-50',
  medium: 'text-amber-600 bg-amber-50',
  low: 'text-blue-600 bg-blue-50',
};

export function SecurityPosture() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [posture, setPosture] = useState<PostureData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    async function load() {
      try {
        const data = await studioSecurity.posture(projectId!);
        setPosture(data as PostureData);
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [projectId]);

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-foreground mb-2">
          ← Back
        </button>
        <h1 className="text-2xl font-bold text-foreground">Security Posture</h1>
        <p className="text-muted-foreground mt-1">App-level security baseline checks</p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-muted animate-pulse rounded-xl" />)}
        </div>
      ) : !posture ? (
        <p className="text-muted-foreground">No security data available.</p>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="border border-border rounded-xl p-4 bg-card text-center">
              <p className={`text-3xl font-bold mb-1 ${posture.score >= 80 ? 'text-green-600' : posture.score >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                {posture.score}
              </p>
              <p className="text-xs text-muted-foreground">Score / 100</p>
            </div>
            <div className="border border-border rounded-xl p-4 bg-card text-center">
              <p className="text-3xl font-bold text-foreground mb-1">{posture.summary.passed}</p>
              <p className="text-xs text-muted-foreground">Passed</p>
            </div>
            <div className="border border-border rounded-xl p-4 bg-card text-center">
              <p className="text-3xl font-bold text-foreground mb-1">{posture.summary.failed}</p>
              <p className="text-xs text-muted-foreground">Failed</p>
            </div>
            <div className="border border-border rounded-xl p-4 bg-card text-center">
              <p className="text-3xl font-bold text-red-600 mb-1">{posture.summary.critical_failed}</p>
              <p className="text-xs text-muted-foreground">Critical</p>
            </div>
          </div>

          <div className="space-y-2">
            {posture.checks.map((check) => (
              <div key={check.id} className={`border rounded-xl p-4 flex items-start gap-3 ${check.passed ? 'border-green-200 bg-green-50/30' : 'border-border bg-card'}`}>
                <span className={`text-lg flex-shrink-0 ${check.passed ? 'text-green-500' : 'text-red-500'}`}>
                  {check.passed ? '✓' : '✗'}
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{check.name}</p>
                    {!check.passed && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${SEVERITY_COLOR[check.severity] ?? ''}`}>
                        {check.severity}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{check.message}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
